import { Component, inject, Input, ChangeDetectionStrategy } from '@angular/core'
import { DatePipe } from '@angular/common'
import { MatProgressBar } from '@angular/material/progress-bar'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { Call, CallProcessingStatus } from '../calls.service'
import { DurationPipe } from '../duration.pipe'
import { MeetingHealthIndicatorComponent } from '../meeting-health-indicator/meeting-health-indicator.component'
import { getMeetingHealthItems } from '../meeting-health-indicator/meeting-health.utils'
import { TemplateItem } from '../template-editor.component/types/template.types'

@Component({
  selector: 'app-call-details-bar',
  standalone: true,
  imports: [DatePipe, MatProgressBar, MatTooltip, TranslatePipe, DurationPipe, MeetingHealthIndicatorComponent],
  templateUrl: './call-details-bar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './call-details-bar.component.scss',
})
export class CallDetailsBarComponent {
  private translateService = inject(TranslateService)

  @Input({ required: true })
  call!: Call

  @Input({ required: true })
  templateItems!: TemplateItem[]

  get isSplitting(): boolean {
    return (
      this.call.totalNumberOfSplits > 0 &&
      (this.call.processingStatus === CallProcessingStatus.SPLITTING_CALL || this.call.processingStatus === CallProcessingStatus.PROCESSING_MEDIA)
    )
  }

  getMeetingHealthItems() {
    return getMeetingHealthItems(this.call, this.templateItems, (key: string, params?: Record<string, unknown>) => this.translateService.instant(key, params))
  }

  // TODO Fix the typing of these methods after correctly transforming dates in the server response on calls.service.ts
  getExpiryText(): string {
    if (this.call.retentionPeriodExpirationDate === null) {
      return this.translateService.instant('CALLS.EXPIRES.NEVER')
    }

    const now = new Date()
    const hoursLeft = Math.max((new Date(this.call.retentionPeriodExpirationDate).getTime() - now.getTime()) / (1000 * 60 * 60), 0)

    if (hoursLeft >= 24) {
      const days = Math.round(hoursLeft / 24)
      const key = days === 1 ? 'CALLS.EXPIRES.DAYS' : 'CALLS.EXPIRES.DAYS_PLURAL'
      return this.translateService.instant(key, { count: days })
    } else if (hoursLeft >= 1.5) {
      const hours = Math.round(hoursLeft)
      const key = hours === 1 ? 'CALLS.EXPIRES.HOURS' : 'CALLS.EXPIRES.HOURS_PLURAL'
      return this.translateService.instant(key, { count: hours })
    } else {
      const minutes = Math.round(hoursLeft * 60)
      const key = minutes === 1 ? 'CALLS.EXPIRES.MINUTES' : 'CALLS.EXPIRES.MINUTES_PLURAL'
      return this.translateService.instant(key, { count: minutes })
    }
  }

  get isExpiryUrgent(): boolean {
    if (this.call.retentionPeriodExpirationDate === null) {
      return false
    }

    const now = new Date()
    const hoursLeft = (new Date(this.call.retentionPeriodExpirationDate).getTime() - now.getTime()) / (1000 * 60 * 60)
    return hoursLeft < 24
  }

  getExpiryTooltip(): string {
    if (this.call.retentionPeriodExpirationDate === null) {
      return this.translateService.instant('CALLS.EXPIRES.TOOLTIP_NEVER')
    }

    const expiryDate = new Date(this.call.retentionPeriodExpirationDate)
    const day = expiryDate.getDate()
    const monthNames = [
      this.translateService.instant('COMMON.MONTHS_SHORT.JAN'),
      this.translateService.instant('COMMON.MONTHS_SHORT.FEB'),
      this.translateService.instant('COMMON.MONTHS_SHORT.MAR'),
      this.translateService.instant('COMMON.MONTHS_SHORT.APR'),
      this.translateService.instant('COMMON.MONTHS_SHORT.MAY'),
      this.translateService.instant('COMMON.MONTHS_SHORT.JUN'),
      this.translateService.instant('COMMON.MONTHS_SHORT.JUL'),
      this.translateService.instant('COMMON.MONTHS_SHORT.AUG'),
      this.translateService.instant('COMMON.MONTHS_SHORT.SEP'),
      this.translateService.instant('COMMON.MONTHS_SHORT.OCT'),
      this.translateService.instant('COMMON.MONTHS_SHORT.NOV'),
      this.translateService.instant('COMMON.MONTHS_SHORT.DEC'),
    ]
    const month = monthNames[expiryDate.getMonth()]
    const year = expiryDate.getFullYear()
    const hours = expiryDate.getHours().toString().padStart(2, '0')
    const minutes = expiryDate.getMinutes().toString().padStart(2, '0')

    return this.translateService.instant('CALLS.EXPIRES.TOOLTIP_DATE', {
      day: day,
      month: month,
      year: year,
      hours: hours,
      minutes: minutes,
    })
  }
}
